import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from './config/api';

function ContractPage({ user, onSigned }) {
  const [contractHtml, setContractHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasRead, setHasRead] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);
  const contractRef = useRef(null);

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/onboarding/contract`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (!res.ok) throw new Error('Failed to load contract');
        const data = await res.json();
        setContractHtml(data.html);
      } catch (err) {
        setError('Unable to load the contract. Please refresh the page or contact your coach.');
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [user.token]);

  const handleScroll = () => {
    const el = contractRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (atBottom) setHasRead(true);
  };

  const handleSign = async (e) => {
    e.preventDefault();
    if (!signedName.trim()) {
      setSignError('Please type your full name to sign.');
      return;
    }
    setSigning(true);
    setSignError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/onboarding/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ signedName: signedName.trim() })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message || 'Failed to record signature');
      }
      onSigned();
    } catch (err) {
      setSignError(err.message);
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-header">
        <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
          <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="header-logo" />
        </a>
        <div className="header-brand-divider" />
        <span className="onboarding-step-label">Step 1 of 4 — Coaching Agreement</span>
      </div>

      <div className="onboarding-body">
        <h1 className="onboarding-title">Review &amp; Sign Your Coaching Agreement</h1>
        <p className="onboarding-subtitle">Read the full agreement below and sign to begin your coaching engagement.</p>

        {loading && <div className="onboarding-loading">Loading agreement...</div>}
        {error && <div className="onboarding-error">{error}</div>}

        {!loading && !error && (
          <>
            <div
              className="contract-scroll-box"
              ref={contractRef}
              onScroll={handleScroll}
            >
              <div
                className="contract-html-content"
                dangerouslySetInnerHTML={{ __html: contractHtml }}
              />
              {!hasRead && (
                <div className="contract-scroll-hint">↓ Scroll to read the full agreement</div>
              )}
            </div>

            <form className="signature-form" onSubmit={handleSign}>
              <div className="signature-block">
                <div className="signature-coach">
                  <p className="signature-label">NextLeader Coaching (COACH)</p>
                  <p className="signature-name">Irwin Tran — Owner</p>
                  <p className="signature-signed">✓ Signed</p>
                </div>

                <div className="signature-client">
                  <p className="signature-label">Client Signature</p>
                  <input
                    type="text"
                    className="signature-input"
                    placeholder="Type your full name"
                    value={signedName}
                    onChange={(e) => setSignedName(e.target.value)}
                    disabled={signing}
                    autoComplete="name"
                  />
                  <p className="signature-legal">
                    By typing your name and clicking "Sign Agreement", you agree that this constitutes your electronic signature and you accept the terms of this coaching agreement.
                  </p>
                  {signError && <p className="onboarding-error">{signError}</p>}
                  <button
                    type="submit"
                    className="onboarding-btn"
                    disabled={signing || !signedName.trim()}
                  >
                    {signing ? 'Signing...' : 'Sign Agreement →'}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ContractPage;
